import { useEffect, useCallback } from 'react';

const STORAGE_KEY = 'scheduled_cache_reset';
const RESET_RETURN_TO_KEY = 'cache_reset_return_to';

interface ScheduledResetConfig {
  enabled: boolean;
  intervalDays: number;
  lastReset: number | null;
  nextReset: number | null;
}

const DEFAULT_CONFIG: ScheduledResetConfig = {
  enabled: false,
  intervalDays: 7,
  lastReset: null,
  nextReset: null,
};

export function getScheduledResetConfig(): ScheduledResetConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_CONFIG;
    return { ...DEFAULT_CONFIG, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_CONFIG;
  }
}

export function setScheduledResetConfig(config: Partial<ScheduledResetConfig>) {
  try {
    const current = getScheduledResetConfig();
    const updated = { ...current, ...config };
    
    // Пересчитываем nextReset если изменился interval или lastReset
    if (updated.lastReset && updated.intervalDays > 0) {
      updated.nextReset = updated.lastReset + (updated.intervalDays * 24 * 60 * 60 * 1000);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save scheduled reset config:', e);
  }
}

export function markCacheResetCompleted() {
  const now = Date.now();
  const config = getScheduledResetConfig();
  setScheduledResetConfig({
    lastReset: now,
    nextReset: config.intervalDays > 0 ? now + (config.intervalDays * 24 * 60 * 60 * 1000) : null,
  });
}

export function useScheduledCacheReset() {
  const checkAndReset = useCallback(() => {
    const config = getScheduledResetConfig();
    
    if (!config.enabled) return;
    if (!config.nextReset) return;
    
    const now = Date.now();
    
    if (now >= config.nextReset) {
      // Сохраняем маршрут возврата, чтобы после reset не зациклиться на /reset
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (!returnTo.startsWith('/reset')) {
        localStorage.setItem(RESET_RETURN_TO_KEY, returnTo || '/');
      }

      // Время пришло - редирект на страницу сброса
      const url = new URL('/reset', window.location.origin);
      url.searchParams.set('auto', '1');
      url.searchParams.set('_cb', String(Date.now()));
      window.location.href = url.toString();
    }
  }, []);

  useEffect(() => {
    // Проверяем сразу при монтировании
    checkAndReset();
    
    // Проверяем каждый час
    const interval = setInterval(checkAndReset, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [checkAndReset]);
}
